/**
 * Frontend-only calculated hidden fields (work in progress).
 *
 * Database persistence for formulas is not wired up yet - the existing SQL
 * score functions are untouched. These helpers power the builder preview and
 * validation only, so a hidden score can reference earlier numeric answers
 * with a Notion-style formula.
 *
 * Supported expression syntax:
 * - question references as {{input_id}}
 * - numbers, + - * / ( )
 * - comparisons > < >= <= == != (evaluate to 1 / 0, useful inside IF)
 * - SUM(...) adds values, e.g. SUM({{a}}, {{b}}, 2). Blank questions count as 0.
 * - AVERAGE(...) is the mean of answered numbers. Blank questions are skipped.
 * - MIN(...) and MAX(...) are the smallest and largest answered numbers.
 * - COUNT(...) counts questions that have a number, including 0. Blank questions are skipped.
 * - ROUND(value) or ROUND(value, places), and ABS(value).
 * - IF(condition, then, else) e.g. IF({{a}} > 5, AVERAGE({{a}}, {{b}}), 0)
 * - the display symbols ÷ and × are normalised to / and * before evaluation
 */

export type HiddenFieldFormulaMode = "none" | "sum" | "custom";

export interface HiddenFieldFormula {
    mode: HiddenFieldFormulaMode;
    sourceInputIds: string[];
    expression: string;
}

export const EMPTY_FORMULA: HiddenFieldFormula = { mode: "none", sourceInputIds: [], expression: "" };

/** Functions offered by the formula autocomplete, with builder hints. */
export const FORMULA_FUNCTIONS = [
    { name: "SUM", hint: "Add values", snippet: "SUM()" },
    { name: "AVERAGE", hint: "Mean of answered numbers", snippet: "AVERAGE()" },
    { name: "MIN", hint: "Smallest number", snippet: "MIN()" },
    { name: "MAX", hint: "Largest number", snippet: "MAX()" },
    { name: "COUNT", hint: "Count answered numbers", snippet: "COUNT()" },
    { name: "ROUND", hint: "Round a number", snippet: "ROUND()" },
    { name: "ABS", hint: "Absolute value", snippet: "ABS()" },
    { name: "IF", hint: "Conditional value", snippet: "IF()" },
] as const;

export type FormulaFunctionName = (typeof FORMULA_FUNCTIONS)[number]["name"];

const NUMERIC_KEYS = new Set(["number", "count", "slider", "scale-rating", "pain-slider", "glucose-slider"]);

export function isNumericInputKey(inputKey: string | null | undefined): boolean {
    return inputKey != null && NUMERIC_KEYS.has(inputKey);
}

export function toFiniteAnswer(value: unknown): number | null {
    if (typeof value === "number" && Number.isFinite(value)) return value;
    if (typeof value === "string" && value.trim() !== "") {
        const parsed = Number(value);
        return Number.isFinite(parsed) ? parsed : null;
    }
    return null;
}

export function sumEarlierValues(answers: Record<string, unknown>, sourceInputIds: string[]): number | null {
    let total = 0;
    let seen = false;
    for (const id of sourceInputIds) {
        const value = toFiniteAnswer(answers[id]);
        if (value == null) continue;
        total += value;
        seen = true;
    }
    return seen ? total : null;
}

/** The editor renders × and ÷ but the stored expression keeps * and /. */
export function normalizeFormulaSymbols(expression: string): string {
    return expression.replace(/÷/g, "/").replace(/×/g, "*").replace(/−/g, "-");
}

const REFERENCE_PATTERN = /\{\{([0-9a-fA-F-]{36}|[A-Za-z0-9_-]+)\}\}/g;

export function listFormulaReferences(expression: string): string[] {
    const refs: string[] = [];
    for (const match of normalizeFormulaSymbols(expression).matchAll(REFERENCE_PATTERN)) {
        if (match[1] && !refs.includes(match[1])) refs.push(match[1]);
    }
    return refs;
}

export function validateCustomFormula(
    expression: string,
    allowedInputIds: readonly string[]
): { ok: true; references: string[] } | { ok: false; error: string; references: string[] } {
    const trimmed = normalizeFormulaSymbols(expression).trim();
    if (!trimmed) return { ok: false, error: "Enter a formula.", references: [] };
    const references = listFormulaReferences(trimmed);
    const allowed = new Set(allowedInputIds);
    const unknown = references.filter((id) => !allowed.has(id));
    if (unknown.length > 0) return { ok: false, error: `Unknown question: ${unknown[0]}.`, references };

    if (/[{}]/.test(trimmed.replace(REFERENCE_PATTERN, ""))) {
        return { ok: false, error: "Question pills look broken. Pick questions from the list.", references };
    }
    const withoutRefs = trimmed.replace(REFERENCE_PATTERN, "1");
    if (/[^0-9+\-*/().,\s<>=!A-Za-z_]/.test(withoutRefs)) {
        return {
            ok: false,
            error: "Only numbers, + - * / ( ) comparisons and the formula functions are allowed.",
            references,
        };
    }
    try {
        const value = evaluateParsedFormula(trimmed, Object.fromEntries(allowedInputIds.map((id) => [id, 1])));
        if (value == null || !Number.isFinite(value)) {
            return { ok: false, error: "That formula does not calculate a number.", references };
        }
    } catch (error) {
        return {
            ok: false,
            error:
                error instanceof Error ? error.message : "That formula could not be read. Check brackets and commas.",
            references,
        };
    }
    return { ok: true, references };
}

type FormulaToken =
    | { type: "number"; value: number }
    | { type: "ref"; id: string }
    | { type: "name"; name: string }
    | { type: "op"; op: string }
    | { type: "lparen" }
    | { type: "rparen" }
    | { type: "comma" };

interface FormulaArg {
    value: number;
    counted: boolean;
}

function answeredValues(args: FormulaArg[]): number[] {
    return args.filter((arg) => arg.counted).map((arg) => arg.value);
}

function callFormulaFunction(name: string, args: FormulaArg[]): number {
    if (name === "SUM") return args.reduce((total, arg) => total + arg.value, 0);
    if (name === "COUNT") return args.filter((arg) => arg.counted).length;
    if (name === "AVERAGE" || name === "MIN" || name === "MAX") {
        const values = answeredValues(args);
        if (values.length === 0) throw new Error(`${name} needs at least one number.`);
        if (name === "AVERAGE") return values.reduce((total, value) => total + value, 0) / values.length;
        return name === "MIN" ? Math.min(...values) : Math.max(...values);
    }
    if (name === "ROUND") {
        if (args.length < 1 || args.length > 2) throw new Error("Write ROUND as ROUND(value) or ROUND(value, places).");
        const places = args.length === 2 ? (args[1]?.value ?? 0) : 0;
        const factor = 10 ** places;
        return Math.round((args[0]?.value ?? 0) * factor) / factor;
    }
    if (name === "ABS") {
        if (args.length !== 1) throw new Error("Write ABS as ABS(value).");
        return Math.abs(args[0]?.value ?? 0);
    }
    if (args.length < 2 || args.length > 3) throw new Error("Write IF as IF(condition, then, else).");
    return (args[0]?.value ?? 0) !== 0 ? (args[1]?.value ?? 0) : (args[2]?.value ?? 0);
}

const FUNCTION_NAMES = new Set<string>(FORMULA_FUNCTIONS.map((fn) => fn.name));

function tokenizeFormula(source: string): FormulaToken[] {
    const tokens: FormulaToken[] = [];
    let pos = 0;
    while (pos < source.length) {
        const rest = source.slice(pos);
        if (/^\s/.test(rest)) {
            pos += 1;
            continue;
        }
        const refMatch = /^\{\{\s*([0-9a-fA-F-]{36}|[A-Za-z0-9_-]+)\s*\}\}/.exec(rest);
        if (refMatch?.[1]) {
            tokens.push({ type: "ref", id: refMatch[1] });
            pos += refMatch[0].length;
            continue;
        }
        const numberMatch = /^(\d+\.?\d*|\.\d+)/.exec(rest);
        if (numberMatch?.[1]) {
            tokens.push({ type: "number", value: Number(numberMatch[1]) });
            pos += numberMatch[1].length;
            continue;
        }
        const nameMatch = /^[A-Za-z_][A-Za-z0-9_]*/.exec(rest);
        if (nameMatch?.[0]) {
            tokens.push({ type: "name", name: nameMatch[0].toUpperCase() });
            pos += nameMatch[0].length;
            continue;
        }
        const opMatch = /^(>=|<=|==|!=|[+\-*/(),><=])/.exec(rest);
        if (opMatch?.[1]) {
            const op = opMatch[1];
            if (op === "(") tokens.push({ type: "lparen" });
            else if (op === ")") tokens.push({ type: "rparen" });
            else if (op === ",") tokens.push({ type: "comma" });
            else tokens.push({ type: "op", op });
            pos += op.length;
            continue;
        }
        throw new Error(`Unexpected "${rest.slice(0, 1)}" in the formula.`);
    }
    return tokens;
}

function evaluateParsedFormula(expression: string, answers: Record<string, unknown>): number {
    const tokens = tokenizeFormula(normalizeFormulaSymbols(expression));
    if (tokens.length === 0) throw new Error("Empty expression");
    let pos = 0;

    const peek = () => tokens[pos];
    const consume = () => tokens[pos++];

    function parseComparison(): number {
        let value = parseAdditive();
        for (;;) {
            const token = peek();
            if (token?.type !== "op" || ![">", "<", ">=", "<=", "==", "=", "!="].includes(token.op)) return value;
            const op = (consume() as { type: "op"; op: string }).op;
            const rhs = parseAdditive();
            if (op === ">") value = value > rhs ? 1 : 0;
            else if (op === "<") value = value < rhs ? 1 : 0;
            else if (op === ">=") value = value >= rhs ? 1 : 0;
            else if (op === "<=") value = value <= rhs ? 1 : 0;
            else if (op === "!=") value = value !== rhs ? 1 : 0;
            else value = value === rhs ? 1 : 0;
        }
    }

    function parseAdditive(): number {
        let value = parseMultiplicative();
        for (;;) {
            const token = peek();
            if (token?.type !== "op" || (token.op !== "+" && token.op !== "-")) return value;
            const op = (consume() as { type: "op"; op: string }).op;
            const rhs = parseMultiplicative();
            value = op === "+" ? value + rhs : value - rhs;
        }
    }

    function parseMultiplicative(): number {
        let value = parseUnary();
        for (;;) {
            const token = peek();
            if (token?.type !== "op" || (token.op !== "*" && token.op !== "/")) return value;
            const op = (consume() as { type: "op"; op: string }).op;
            const rhs = parseUnary();
            value = op === "*" ? value * rhs : value / rhs;
        }
    }

    function parseUnary(): number {
        const token = peek();
        if (token?.type === "op" && (token.op === "+" || token.op === "-")) {
            const op = (consume() as { type: "op"; op: string }).op;
            const value = parseUnary();
            return op === "+" ? value : -value;
        }
        return parsePrimary();
    }

    function parseArgs(): Array<{ value: number; counted: boolean }> {
        const args: Array<{ value: number; counted: boolean }> = [];
        if (peek()?.type === "rparen") {
            consume();
            return args;
        }
        for (;;) {
            const start = pos;
            const value = parseComparison();
            const slice = tokens.slice(start, pos);
            const bareRef = slice.length === 1 && slice[0]?.type === "ref" ? slice[0].id : null;
            const counted = bareRef != null ? toFiniteAnswer(answers[bareRef]) != null : Number.isFinite(value);
            args.push({ value, counted });
            const next = peek();
            if (next?.type === "comma") {
                consume();
                continue;
            }
            if (next?.type === "rparen") {
                consume();
                return args;
            }
            throw new Error("Use commas between values, e.g. SUM({{a}}, {{b}}).");
        }
    }

    function parsePrimary(): number {
        const token = consume();
        if (!token) throw new Error("That formula ends before the maths is finished.");
        if (token.type === "number") return token.value;
        if (token.type === "ref") return toFiniteAnswer(answers[token.id]) ?? 0;
        if (token.type === "lparen") {
            const value = parseComparison();
            if (consume()?.type !== "rparen") throw new Error("Missing ).");
            return value;
        }
        if (token.type === "name") {
            if (!FUNCTION_NAMES.has(token.name)) {
                throw new Error(`Unknown word "${token.name}". Use a formula function, or pick a question.`);
            }
            if (peek()?.type !== "lparen") throw new Error(`Write ${token.name} with brackets, e.g. ${token.name}().`);
            consume();
            const args = parseArgs();
            return callFormulaFunction(token.name, args);
        }
        throw new Error("That formula could not be read. Check brackets and commas.");
    }

    const value = parseComparison();
    if (pos !== tokens.length) throw new Error("That formula has extra text at the end.");
    return value;
}

export function evaluateCustomFormula(expression: string, answers: Record<string, unknown>): number | null {
    if (!normalizeFormulaSymbols(expression).trim()) return null;
    try {
        const value = evaluateParsedFormula(expression, answers);
        return Number.isFinite(value) ? value : null;
    } catch {
        return null;
    }
}

/** Display a calculated result to at most two decimal places. */
export function formatFormulaResult(value: number): string {
    const rounded = Math.round((value + Number.EPSILON) * 100) / 100;
    if (!Number.isFinite(rounded)) return " - ";
    return String(Object.is(rounded, -0) ? 0 : rounded);
}

export function previewHiddenFieldValue(formula: HiddenFieldFormula, answers: Record<string, unknown>): number | null {
    if (formula.mode === "sum") return sumEarlierValues(answers, formula.sourceInputIds);
    if (formula.mode === "custom") return evaluateCustomFormula(formula.expression, answers);
    return null;
}
